/**
 * Module dependencies.
 */

import { RewardsPointsInline } from 'src/components/landingpage/components/rewards-points-inline/rewards-points-inline';
import { useTranslation } from 'next-i18next';
import ArrowTag from 'src/assets/landingpage/arrow-tag.svg';
import styled from 'styled-components';

/**
 * `Container` styled component.
 */

const Container = styled.div`
  /* stylelint-disable no-descending-specificity */
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 60px;
  justify-content: center;
  margin-top: 80px;
  padding: 0 192px;
  position: relative;
  width: 1440px;

  @media (max-width: 1023px) {
    align-items: center;
    flex-direction: column;
    gap: 16px;
    height: auto;
    padding: 0 16px;
    width: 100vw;
  }

  @media (max-width: 767px) {
    margin-top: 32px;
  }

  @media (min-width: 564px) and (max-width: 767px) {
    max-width: 500px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    max-width: 575px;
  }

  @media (min-width: 1024px) and (max-width: 1440px) {
    padding: 0 32px;
    width: 100vw;
  }

  /* Price tag container: column so disclaimer sits below blue + arrow row */
  .price-tag {
    align-items: flex-start;
    display: flex;
    flex: 1;
    flex-direction: column;
    max-width: 704px;
    position: relative;

    @media (max-width: 1023px) {
      width: 100%;
    }

    @media (max-width: 767px) {
      margin-top: 0;
      max-width: none;
      width: 100%;
    }
  }

  /* Single row: navy block + arrow (arrow height matches blue block only) */
  .price-tag-top {
    align-items: stretch;
    display: flex;
    flex-direction: row;
    min-height: 1px;
    min-width: 1px;
    width: 100%;
  }

  /* Price tag body */
  .price-tag-body {
    align-items: flex-start;
    background: #0a0a28;
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 1px;
    min-width: 1px;
    padding: 16px 16px 16px 32px;
    position: relative;

    @media (max-width: 767px) {
      padding: 12px 0;
    }

    @media (min-width: 376px) and (max-width: 400px) {
      padding: 12px 0;
    }

    @media (max-width: 375px) {
      padding: 12px 0;
    }
  }

  /* Price content */
  .price-content {
    display: flex;
    justify-content: space-between;
    position: relative;
    width: 100%;
  }

  /* Legal / disclaimer below blue block (page background) */
  .price-tag-info {
    box-sizing: border-box;
    color: rgba(10, 10, 40, 0.85);
    font-family: Raleway, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    margin: 8px 0 0;
    max-width: 100%;
    width: 100%;

    @media (min-width: 1024px) {
      max-width: 366px;
    }

    @media (max-width: 767px) {
      margin-left: 0;
      margin-right: 0;
      padding-right: 16px;
    }

    @media (max-width: 375px) {
      font-size: 11px;
      line-height: 16px;
    }
  }

  /* Price text */
  .price-text {
    align-items: center;
    color: white;
    display: flex;
    font-family: Raleway, sans-serif;
    font-size: 34px;
    font-weight: 900;
    line-height: 36px;
    min-width: 260px;
    position: relative;

    @media (max-width: 767px) {
      flex: 0.6;
      margin-left: 16px;
      margin-right: 0;
      min-width: 0;
    }

    @media (min-width: 376px) and (max-width: 767px) {
      font-size: 24px;
      line-height: 32px;
    }

    @media (min-width: 376px) and (max-width: 400px) {
      font-weight: 600;
    }

    @media (max-width: 375px) {
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
    }
  }

  /* Separator */
  .separator {
    align-items: center;
    background-color: white;
    display: flex;
    justify-content: center;
    margin: 0 16px;
    position: relative;
    width: 3px;

    @media (max-width: 767px) {
      margin: 0 8px;
      width: 2px;
    }

    @media (max-width: 419px) {
      margin: 0 8px;
      width: 2px;
    }
  }

  /* Price value container */
  .price-value {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    text-align: center;
    width: 126px;

    @media (max-width: 767px) {
      flex: 0.4;
      width: 100%;
    }
  }

  /* Price amount */
  .price-amount {
    color: #00cddc;
    font-family: Raleway, sans-serif;
    font-size: 42px;
    font-weight: 900;
    line-height: 44px;
    width: 100%;

    @media (max-width: 767px) {
      text-align: center;
    }

    @media (max-width: 375px) {
      font-size: 28px;
      line-height: 32px;
    }
  }

  /* VAT text */
  .vat-text {
    color: white;
    font-family: Raleway, sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    white-space: nowrap;

    @media (max-width: 767px) {
      text-align: center;
      width: 100%;
    }

    @media (max-width: 375px) {
      font-size: 11px;
      line-height: 16px;
    }
  }

  /* Arrow SVG */
  .price-arrow {
    align-self: stretch;
    position: relative;
    width: 61.998px;

    @media (max-width: 1023px) {
      display: none;
    }

    svg {
      display: block;
      height: 100%;
      width: 100%;
    }
  }

  /* Button */
  a {
    align-items: flex-start;
    background: white;
    border: 0;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 24px;
    position: relative;
    text-decoration: none;

    > div {
      color: #0a0a28;
      display: flex;
      flex-direction: column;
      font-family: Raleway, sans-serif;
      font-size: 16px;
      font-weight: 900;
      justify-content: center;
      line-height: 0;
      position: relative;
      text-align: center;
      text-transform: uppercase;
      white-space: nowrap;

      p {
        line-height: 24px;
        white-space: pre;
      }
    }

    &:hover {
      background: #0a0a28;

      > div {
        color: white;
      }
    }

    @media (max-width: 767px) {
      width: auto;
    }
  }

  /* Button wrapper */
  > div:last-child {
    align-items: center;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    justify-content: center;
    position: relative;

    @media (max-width: 767px) {
      width: 100%;
    }
  }

  /* stylelint-enable no-descending-specificity */
`;

/**
 * `HeroCallToAction` component.
 */

export function HeroCallToAction() {
  const { t } = useTranslation('landingpage');

  return (
    <Container data-name={'section'} id={'hero-cta-section'}>
      <div
        className={'price-tag'}
        data-name={'pricing'}
        id={'price-tag'}
        style={{
          opacity: 0,
          transform: 'translateY(150px)',
          transition: 'transform 0.1s ease-out, opacity 0.1s ease-out'
        }}
      >
        <div className={'price-tag-top'} data-name={'price-tag-top'}>
          <div className={'price-tag-body'} data-name={'left-container'}>
            <div className={'price-content'} data-name={'Copy + preco'} id={'price-tag-content'}>
              <p className={'price-text'} id={'price-tag-text'}>
                {t('cta.priceTag.text')}
              </p>

              <div className={'separator'} id={'price-tag-separator'} />

              <div className={'price-value'} data-name={'preco'} id={'price-tag-price'}>
                <p className={'price-amount'}>{t('cta.priceTag.price')}</p>

                <p className={'vat-text'}>{t('cta.priceTag.vat')}</p>

                <RewardsPointsInline label={t('cta.priceTag.points')} />
              </div>
            </div>
          </div>

          <div className={'price-arrow'} data-name={'Vector'} id={'price-tag-arrow'}>
            <ArrowTag />
          </div>
        </div>

        <p className={'price-tag-info'} data-name={'info'} id={'price-tag-info'}>
          {t('cta.priceTag.info')}
        </p>
      </div>

      <div data-name={'button'}>
        <div
          id={'hero-cta-actions'}
          style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            opacity: 0,
            transform: 'translateY(150px)',
            transition: 'transform 0.1s ease-out, opacity 0.1s ease-out'
          }}
        >
          <a
            aria-label={t('cta.button')}
            data-name={'BTN'}
            href={'/agendar?canal=santander'}
            id={'btn-agendar'}
            style={{
              boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.25)',
              transition: 'boxShadow 0.1s ease-out'
            }}
          >
            <div>
              <p>{t('cta.button')}</p>
            </div>
          </a>

          <RewardsPointsInline id={'hero-cta-points'} label={t('cta.priceTag.points')} placement={'ctaBelow'} />
        </div>
      </div>
    </Container>
  );
}
